import React, { useState, useEffect, useMemo } from "react";
import { API_BASE } from "../api/config";
import Layout from "../components/Layout";
import { useTable, useSortBy, useGlobalFilter, usePagination } from "react-table";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "../context/AuthContext";
import {
  FaTable,
  FaSearch,
  FaFileExcel,
  FaFileCsv,
  FaFilePdf,
  FaCalendarAlt,
  FaPrint,
} from "react-icons/fa";

// helper to convert YYYY-MM-DD -> DD-MM-YYYY
function formatDMY(isoDate) {
  if (!isoDate) return "-";
  const [y, m, d] = isoDate.split("-");
  return `${d}-${m}-${y}`;
}

function Kots() {
  const { branch } = useAuth();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [selectedGroup, setSelectedGroup] = useState("all");

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const todayStr = `${yyyy}-${mm}-${dd}`;

  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);
  const [searchText, setSearchText] = useState("");
  
  const [kotStatusFilter, setKotStatusFilter] = useState("all"); // "all" or "cancel"


  const statusMap = {
    1: "Placed",
    2: "Accepted",
    3: "Ready",
    4: "Served",
    5: "Cancel",
  };
  const statusColors = {
    Placed: "bg-yellow-500",
    Accepted: "bg-green-500",
    Ready: "bg-blue-500",
    Served: "bg-green-500",
    Cancel: "bg-red-500",
  };

  const calcProcessingTime = (placed, served) => {
    if (!placed || !served) return "-";
    const start = new Date(`1970-01-01T${placed}`);
    const end = new Date(`1970-01-01T${served}`);
    const diff = end - start;
    if (isNaN(diff) || diff < 0) return "-";
    const m = Math.floor(diff / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${m}m ${s}s`;
  };

  // Fetch Courses & Groups for dropdowns
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await fetch(
          `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=courses`
        );
        const result = await res.json();
        setCourses(Array.isArray(result) ? result : []);
      } catch (e) {
        console.error("Error fetching courses:", e);
      }
    };

    const fetchGroups = async () => {
      try {
        const res = await fetch(
          `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=groups`
        );
        const result = await res.json();
        setGroups(Array.isArray(result) ? result : []);
      } catch (e) {
        console.error("Error fetching groups:", e);
      }
    };

    fetchCourses();
    fetchGroups();
  }, []);

  const fetchKots = async (from = fromDate, to = toDate) => {
    if (!from || !to) return;
    setLoading(true);
    try {
      const url = `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=get_date_wise_kots_by_admin` +
      `&from_date=${from}&to_date=${to}` +
      `&course_name=${encodeURIComponent(selectedCourse)}` +
      `&group_name=${encodeURIComponent(selectedGroup)}`;
      const res = await fetch(url);
      const result = await res.json();
      const rows = Array.isArray(result) ? result : result?.data || [];

      let formatted = rows.map((item, idx) => ({
        sno: idx + 1,
        kot_date: formatDMY(item.kot_date),
        table_name: item.table_name,
        course_name: item.course_name || "-",
        group_name: item.group_name || "-",
        item_name: item.item_name,
        item_qty: item.item_qty,
        total_amount: parseFloat(item.total_amount || 0),
        kot_running_status: statusMap[item.kot_running_status] || "Unknown",
        remark_prod: item.remark_prod || "",
        initiated: item.kot_time,
        received: item.recieved_time,
        ready: item.ready_time,
        delivered: item.delivered_time,
        processing_time: calcProcessingTime(item.kot_time, item.delivered_time),
        order_ref: item.order_ref,
      }));

      // Apply course/group filters
      if (selectedCourse !== "all") {
        formatted = formatted.filter(
          (r) => r.course_name.toLowerCase() === selectedCourse.toLowerCase()
        );
      }
      if (selectedGroup !== "all") {
        formatted = formatted.filter(
          (r) => r.group_name.toLowerCase() === selectedGroup.toLowerCase()
        );
      }

      setData(formatted);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (e) {
      console.error("Fetch error:", e);
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchKots(todayStr, todayStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

const filteredData = useMemo(() => {
  let tempData = data.filter(
    (row) =>
      row.table_name.toLowerCase().includes(searchText.toLowerCase()) ||
      row.item_name.toLowerCase().includes(searchText.toLowerCase()) ||
      row.course_name.toLowerCase().includes(searchText.toLowerCase()) ||
      row.group_name.toLowerCase().includes(searchText.toLowerCase())
  );

  if (kotStatusFilter === "cancel") {
    tempData = tempData.filter((r) => r.kot_running_status === "Cancel");
  }

  return tempData;
}, [data, searchText, kotStatusFilter]);


  // Totals
  const totalCancel = filteredData
    .filter((r) => r.kot_running_status === "Cancel")
    .reduce((sum, r) => sum + r.total_amount, 0);

  const totalNonCancel = filteredData
    .filter((r) => r.kot_running_status !== "Cancel")
    .reduce((sum, r) => sum + r.total_amount, 0);

  // ===== Export helpers =====
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DayKots");
    XLSX.writeFile(wb, "DayKots.xlsx");
  };

  const exportCSV = () => {
    const csv = [
      [
        "SNo",
        "Date",
        "Table",
        "Origin",
        "Group",
        "Item",
        "Qty",
        "Amount",
        "Status",
        "Placed",
        "Accepted",
        "Ready",
        "Served",
        "Processing Time",
      ],
      ...filteredData.map((r) => [
        r.sno,
        r.kot_date,
        r.table_name,
        r.course_name,
        r.group_name,
        r.item_name,
        r.item_qty,
        r.total_amount,
        r.kot_running_status,
        r.initiated,
        r.received,
        r.ready,
        r.delivered,
        r.processing_time,
      ]),
    ]
      .map((r) => r.join(","))
      .join("\n");
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "DayKots.csv");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Day KOT Report", 105, 15, { align: "center" });
    doc.text(`From: ${formatDMY(fromDate)}  To: ${formatDMY(toDate)}`, 105, 22, {
      align: "center",
    });
    autoTable(doc, {
      head: [
        [
          "SNo",
          "Date",
          "Table",
          "Origin",
          "Group",
          "Item",
          "Qty",
          "Amount",
          "Status",
          "Placed",
          "Accepted",
          "Ready",
          "Served",
          "Processing Time",
        ],
      ],
      body: filteredData.map((r) => [
        r.sno,
        r.kot_date,
        r.table_name,
        r.course_name,
        r.group_name,
        r.item_name,
        r.item_qty,
        r.total_amount,
        r.kot_running_status,
        r.initiated,
        r.received,
        r.ready,
        r.delivered,
        r.processing_time,
      ]),
      startY: 30,
    });
    doc.save("DayKots.pdf");
  };

const printKotByOrderRef = async (order_ref) => {
  try {
    const res = await fetch(
      `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=get_kot_by_order_ref&order_ref=${order_ref}`
    );
    const result = await res.json();

    if (!Array.isArray(result) || result.length === 0) {
      alert("No KOT details found.");
      return;
    }

    const tableName = result[0].table_name || "-";
    const kotDate = result[0].kot_date || "-";
    const kotTime = result[0].kot_time || "-";

    // Build table rows with item-level cancel highlighting
    const kotItems = result
      .map((i) => {
        const isCancelled = i.kot_running_status === "5" || i.kot_running_status === "Cancel";
        return `<tr style="${isCancelled ? 'background: rgba(255,0,0,0.2); text-decoration: line-through;' : ''}">
                  <td>${i.item_qty}</td>
                  <td>${i.item_name}</td>
                </tr>`;
      })
      .join("");
	  
	    // 2️⃣ FETCH day_srno using order_ref
    const srRes = await fetch(
      `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=get_kot_by_order_ref&order_ref=${order_ref}`
    );
    const srData = await srRes.json();
    const daySrNo = srData?.[0]?.day_srno || "—";

    const printContent = `
      <html>
      <head>
        <title>KOT - ${tableName}</title>
        <style>
          body { font-family: monospace; width: 300px; padding: 10px; }
		  .circle {
              width: 40px;
              height: 40px;
              border: 2px solid #000;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 18px;
              margin: 0 auto 10px auto;
              font-weight: bold;
            }
          h2, h3 { text-align: center; margin: 0; padding: 0; }
          .header { margin-bottom: 10px; text-align: center; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          td { padding: 4px 2px; font-size: 20px; font-weight: bold; }
        </style>
      </head>
      <body>
	  <div class="circle">${daySrNo}</div>
        <div class="header">
          <h2>KOT Reprint ${result.some(i => i.kot_running_status === "5" || i.kot_running_status === "Cancel") ? '- CANCELLED' : ''}</h2>
          <h3>Table: ${tableName}</h3>
        </div>
        <div>${kotDate} ${kotTime}</div>
        <table>${kotItems}</table>
        <script>
          window.onload = () => { 
            window.print(); 
            setTimeout(() => window.close(), 2000); 
          };
        </script>
      </body>
      </html>
    `;

    const w = window.open("", "_blank");
    w.document.write(printContent);
    w.document.close();
  } catch (e) {
    console.error("Print error", e);
  }
};




  const columns = useMemo(
    () => [
      { Header: "S.No", accessor: "sno" },
      { Header: "Date", accessor: "kot_date" },
      { Header: "Table", accessor: "table_name" },
      { Header: "Origin", accessor: "course_name" },
      { Header: "Group", accessor: "group_name" },
      { Header: "Item", accessor: "item_name" },
      { Header: "Qty", accessor: "item_qty" },
      { Header: "Amount", accessor: "total_amount" },
      {
        Header: "Status",
        accessor: "kot_running_status",
        Cell: ({ row }) => {
          const status = row.original.kot_running_status;
          const remark = row.original.remark_prod;
          return (
            <div>
              <span
                className={`px-2 py-1 text-xs rounded text-white ${
                  statusColors[status] || "bg-gray-500"
                }`}
              >
                {status}
              </span>
              {remark && (
                <div className="text-xs text-red-300 mt-1 italic">{remark}</div>
              )}
            </div>
          );
        },
      },
      { Header: "Placed", accessor: "initiated" },
      { Header: "Accepted", accessor: "received" },
      { Header: "Ready", accessor: "ready" },
      { Header: "Served", accessor: "delivered" },
      { Header: "Processing Time", accessor: "processing_time" },
      {
        Header: "Actions",
        accessor: "order_ref",
        Cell: ({ value }) => (
          <button
            onClick={() => printKotByOrderRef(value)}
            className="px-2 py-1 text-xs bg-purple-600 text-white rounded flex items-center gap-1"
          >
            <FaPrint /> Reprint
          </button>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
    setGlobalFilter,
  } = useTable(
    { columns, data: filteredData, initialState: { pageSize: 10 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    setGlobalFilter(searchText || undefined);
  }, [searchText, setGlobalFilter]);

  return (
    <Layout>
      <div className="p-4 bg-gradient-to-b from-blue-900 to-black min-h-screen text-white text-sm">
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded shadow-lg text-black">
              Loading...
            </div>
          </div>
        )}

        <h2 className="text-xl font-bold mb-3 flex items-center">
          <FaTable className="mr-2" />  KOT Report{" "}
          {lastUpdated && `(Last updated: ${lastUpdated})`}
        </h2>

        {/* Filters + Buttons */}
        <div className="flex flex-wrap gap-2 mb-3 items-center">
          <div className="flex items-center gap-1">
            <FaCalendarAlt />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border rounded p-1 text-black"
            />
          </div>
          <div className="flex items-center gap-1">
            <FaCalendarAlt />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border rounded p-1 text-black"
            />
          </div>

          {/* Dropdowns */}
          <select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            className="border rounded p-1 text-black"
          >
            <option value="all">All Origins</option>
            {courses.map((c) => (
              <option key={c.item_name} value={c.item_name}>
                {c.item_name}
              </option>
            ))}
          </select>

          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            className="border rounded p-1 text-black"
          >
            <option value="all">All Groups</option>
            {groups.map((g) => (
              <option key={g.item_name} value={g.item_name}>
                {g.item_name}
              </option>
            ))}
          </select>
			<select
				  value={kotStatusFilter}
				  onChange={(e) => setKotStatusFilter(e.target.value)}
				  className="border rounded p-1 text-black"
				>
				  <option value="all">All KOTs</option>
				  <option value="cancel">Only Cancelled</option>
				</select>

          <button
            onClick={() => fetchKots()}
            className="bg-green-600 px-3 py-1 rounded text-white text-sm"
          >
            Fetch Data
          </button>

          <div className="flex items-center gap-1">
            <FaSearch />
            <input
              type="text"
              placeholder="Search Table / Item / Origin / Group"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="border rounded p-1 text-black"
            />
          </div>
          <button
            onClick={exportExcel}
            className="bg-green-600 px-3 py-1 rounded flex items-center gap-1 text-sm"
          >
            <FaFileExcel /> Excel
          </button>
          <button
            onClick={exportCSV}
            className="bg-blue-600 px-3 py-1 rounded flex items-center gap-1 text-sm"
          >
            <FaFileCsv /> CSV
          </button>
          <button
            onClick={exportPDF}
            className="bg-red-600 px-3 py-1 rounded flex items-center gap-1 text-sm"
          >
            <FaFilePdf /> PDF
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table
            {...getTableProps()}
            className="min-w-full border border-gray-700"
          >
            <thead className="bg-blue-800">
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()}>
                  {hg.headers.map((col) => (
                    <th
                      {...col.getHeaderProps(col.getSortByToggleProps())}
                      className="border px-2 py-1 text-left"
                    >
                      {col.render("Header")}
                      {col.isSorted
                        ? col.isSortedDesc
                          ? " 🔽"
                          : " 🔼"
                        : ""}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className="hover:bg-blue-900 even:bg-blue-950"
                  >
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="border px-2 py-1">
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="mt-4 text-right font-bold">
          <div>Cancelled Total: ₹{totalCancel.toFixed(2)}</div>
          <div>Non-Cancelled Total: ₹{totalNonCancel.toFixed(2)}</div>
        </div>

        {/* Pagination */}
        <div className="flex justify-between mt-2">
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="px-3 py-1 border rounded bg-blue-700"
          >
            Previous
          </button>
          <span>
            Page {pageIndex + 1} of {pageOptions.length}
          </span>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="px-3 py-1 border rounded bg-blue-700"
          >
            Next
          </button>
        </div>
      </div>
    </Layout>
  );
}

export default Kots;
